import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
} from "firebase/firestore";
import moment from "moment";
import { Card } from "@mui/material";
import Swal from "sweetalert2";

// Brand Color Palette
const COLORS = {
  darkGreen: "#06362D",
  calPolyGreen: "#155332",
  vanilla: "#DEE2B1",
  gold: "#FFD500",
  goldenrod: "#D1A505",
  bgDark: "#031c17",
};

const TEXT_SECONDARY = "rgba(222, 226, 177, 0.7)";
const WHATSAPP_RATE_PER_MSG = 1.5;

const Toast = Swal.mixin({
  toast: true,
  position: "top-end",
  showConfirmButton: false,
  timer: 2000,
  timerProgressBar: true,
});

function WalletHistory() {
  const { propertyId } = useParams();

  const [walletCredits, setWalletCredits] = useState([]);
  const [whatsappDailyDebits, setWhatsappDailyDebits] = useState([]);
  const [totalWhatsappMsgCount, setTotalWhatsappMsgCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const [filterType, setFilterType] = useState("ALL"); // ALL | CREDIT | DEBIT
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  // 1. Fetch & Aggregate WhatsApp History Date-Wise for Debit Entries
  useEffect(() => {
    if (!propertyId) return;

    const fetchWhatsappLogs = async () => {
      try {
        const waQuery = query(
          collection(db, "whatsapphistory"),
          where("propertyId", "==", propertyId),
          orderBy("createdAt", "desc")
        );

        const waSnap = await getDocs(waQuery);
        setTotalWhatsappMsgCount(waSnap.size);

        // Group messages by Date (YYYY-MM-DD)
        const dateGroupMap = {};

        waSnap.docs.forEach((docSnap) => {
          const item = docSnap.data();
          let rawDate = item.createdAt?.toDate ? item.createdAt.toDate() : new Date();
          const dateKey = moment(rawDate).format("YYYY-MM-DD");

          if (!dateGroupMap[dateKey]) {
            dateGroupMap[dateKey] = {
              dateKey,
              displayDate: moment(rawDate).format("DD MMM YYYY"),
              latestTimestamp: rawDate.getTime(),
              count: 0,
            };
          }
          dateGroupMap[dateKey].count += 1;
        });

        // Convert grouped dates to unified Debit statement objects
        const debitEntries = Object.values(dateGroupMap).map((entry) => {
          const totalCost = entry.count * WHATSAPP_RATE_PER_MSG;
          return {
            id: `wa-debit-${entry.dateKey}`,
            timestamp: entry.latestTimestamp,
            formattedDate: `${entry.displayDate} (Daily Total)`,
            type: "DEBIT",
            description: `WhatsApp Messaging (${entry.count} msgs × ₹${WHATSAPP_RATE_PER_MSG.toFixed(2)})`,
            paymentId: `WA-ALERT-${entry.dateKey}`,
            msgCount: entry.count,
            amount: totalCost,
            status: "Deducted",
          };
        });

        setWhatsappDailyDebits(debitEntries);
      } catch (err) {
        console.error("Error aggregating WhatsApp logs:", err);
      }
    };

    fetchWhatsappLogs();
  }, [propertyId]);

  // 2. Real-time Listener for Wallet Recharge History (Credits)
  useEffect(() => {
    if (!propertyId) {
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "properties", propertyId, "wallet_history"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const creditEntries = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          const rawDate = data.createdAt?.toDate ? data.createdAt.toDate() : new Date();
          return {
            id: docSnap.id,
            timestamp: rawDate.getTime(),
            formattedDate: moment(rawDate).format("DD MMM YYYY, hh:mm A"),
            type: "CREDIT",
            description: data.description || "Wallet Recharge via Razorpay",
            paymentId: data.paymentId || "Direct Top-Up",
            amount: Number(data.amount || 0),
            status: data.status || "Success",
          };
        });

        setWalletCredits(creditEntries);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching wallet history:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [propertyId]);

  // Copy Reference ID
  const handleCopyId = (id) => {
    if (!id || id === "N/A") return;
    navigator.clipboard.writeText(id);
    Toast.fire({
      icon: "success",
      title: "Reference ID copied",
    });
  };

  // 3. Merged Unified Statement Feed sorted by Date Descending
  const mergedHistory = useMemo(() => {
    const combined = [...walletCredits, ...whatsappDailyDebits];
    return combined.sort((a, b) => b.timestamp - a.timestamp);
  }, [walletCredits, whatsappDailyDebits]);

  // 4. Ledger Metrics (Allows Negative Balances)
  const metrics = useMemo(() => {
    const totalRecharged = walletCredits.reduce((sum, item) => sum + Number(item.amount || 0), 0);
    const totalWhatsappCost = totalWhatsappMsgCount * WHATSAPP_RATE_PER_MSG;
    const remainingBalance = totalRecharged - totalWhatsappCost;

    return {
      totalRecharged,
      totalCreditsCount: walletCredits.length,
      totalWhatsappCost,
      totalWhatsappMsgCount,
      remainingBalance,
    };
  }, [walletCredits, totalWhatsappMsgCount]);

  // 5. Filter & Pagination
  const filteredRecords = useMemo(() => {
    return mergedHistory.filter((item) => {
      const matchesType =
        filterType === "ALL" || item.type.toUpperCase() === filterType;
      const matchesSearch =
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.paymentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.formattedDate.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesType && matchesSearch;
    });
  }, [mergedHistory, filterType, searchTerm]);

  const totalPages = Math.ceil(filteredRecords.length / rowsPerPage) || 1;
  const currentRows = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredRecords.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRecords, currentPage]);

  return (
    <div
      style={{
        background: COLORS.bgDark,
        minHeight: "100vh",
        padding: "30px 24px 100px",
        color: COLORS.vanilla,
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <div className="container-fluid" style={{ maxWidth: "1320px" }}>
        {/* HEADER TITLE (WITHOUT PROPERTY ID OR BACK BUTTON) */}
        <div className="mb-4">
          <h4 style={{ margin: 0, fontWeight: 800, color: COLORS.gold, fontSize: "1.6rem", letterSpacing: "0.5px" }}>
            Hotel Wallet & Messaging Statement
          </h4>
          <p style={{ margin: "4px 0 0", fontSize: "13px", color: TEXT_SECONDARY }}>
            Consolidated statement of wallet top-ups and date-wise WhatsApp alert usage
          </p>
        </div>

        {/* METRICS / STAT CARDS */}
        <div className="row g-3 mb-4">
          {/* TOTAL RECHARGED */}
          <div className="col-12 col-md-4">
            <Card
              style={{
                borderRadius: "18px",
                border: `1px solid ${COLORS.calPolyGreen}`,
                background: `linear-gradient(135deg, ${COLORS.darkGreen} 0%, ${COLORS.bgDark} 100%)`,
                padding: "22px",
                boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div style={{ position: "absolute", top: 0, left: 0, width: "5px", height: "100%", background: COLORS.vanilla }} />
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span style={{ fontSize: "11px", color: TEXT_SECONDARY, textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.5px" }}>
                    Total Recharged
                  </span>
                  <h3 style={{ margin: "6px 0 0", color: COLORS.vanilla, fontWeight: 800, fontSize: "1.85rem" }}>
                    ₹{metrics.totalRecharged.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </h3>
                  <div style={{ fontSize: "11px", color: TEXT_SECONDARY, marginTop: "6px" }}>
                    <span style={{ color: COLORS.gold, fontWeight: 700 }}>{metrics.totalCreditsCount}</span> top-up deposits completed
                  </div>
                </div>
                <div
                  style={{
                    width: "46px",
                    height: "46px",
                    borderRadius: "14px",
                    background: COLORS.calPolyGreen,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className="fas fa-wallet" style={{ color: COLORS.vanilla, fontSize: "18px" }}></i>
                </div>
              </div>
            </Card>
          </div>

          {/* TOTAL DEBIT (WHATSAPP CONSUMPTION) */}
          <div className="col-12 col-md-4">
            <Card
              style={{
                borderRadius: "18px",
                border: `1px solid ${COLORS.calPolyGreen}`,
                background: `linear-gradient(135deg, ${COLORS.darkGreen} 0%, ${COLORS.bgDark} 100%)`,
                padding: "22px",
                boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div style={{ position: "absolute", top: 0, left: 0, width: "5px", height: "100%", background: COLORS.goldenrod }} />
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span style={{ fontSize: "11px", color: TEXT_SECONDARY, textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.5px" }}>
                    WhatsApp Debits ({metrics.totalWhatsappMsgCount} msgs)
                  </span>
                  <h3 style={{ margin: "6px 0 0", color: COLORS.goldenrod, fontWeight: 800, fontSize: "1.85rem" }}>
                    ₹{metrics.totalWhatsappCost.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </h3>
                  <div style={{ fontSize: "11px", color: TEXT_SECONDARY, marginTop: "6px" }}>
                    Rate: <span style={{ color: COLORS.gold, fontWeight: 700 }}>₹1.50</span> per WhatsApp message
                  </div>
                </div>
                <div
                  style={{
                    width: "46px",
                    height: "46px",
                    borderRadius: "14px",
                    background: COLORS.calPolyGreen,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className="fab fa-whatsapp" style={{ color: COLORS.goldenrod, fontSize: "22px" }}></i>
                </div>
              </div>
            </Card>
          </div>

          {/* REMAINING WALLET BALANCE (CAN BE NEGATIVE) */}
          <div className="col-12 col-md-4">
            <Card
              style={{
                borderRadius: "18px",
                border: `1px solid ${metrics.remainingBalance < 0 ? "#ff4d4f" : COLORS.gold}`,
                background: `linear-gradient(135deg, ${COLORS.darkGreen} 0%, ${COLORS.bgDark} 100%)`,
                padding: "22px",
                boxShadow: metrics.remainingBalance < 0 ? "0 6px 20px rgba(255, 77, 79, 0.2)" : "0 6px 20px rgba(255, 213, 0, 0.15)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "5px",
                  height: "100%",
                  background: metrics.remainingBalance < 0 ? "#ff4d4f" : `linear-gradient(180deg, ${COLORS.gold}, ${COLORS.goldenrod})`,
                }}
              />
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span style={{ fontSize: "11px", color: metrics.remainingBalance < 0 ? "#ff8082" : COLORS.gold, textTransform: "uppercase", fontWeight: 800, letterSpacing: "0.5px" }}>
                    Available Remaining Balance
                  </span>
                  <h3
                    style={{
                      margin: "6px 0 0",
                      color: metrics.remainingBalance < 0 ? "#ff6b6b" : COLORS.gold,
                      fontWeight: 800,
                      fontSize: "1.85rem",
                    }}
                  >
                    {metrics.remainingBalance < 0 ? "-" : ""}₹
                    {Math.abs(metrics.remainingBalance).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                  </h3>
                  <div style={{ fontSize: "11px", color: TEXT_SECONDARY, marginTop: "6px" }}>
                    {metrics.remainingBalance < 0 ? (
                      <span style={{ color: "#ff8082", fontWeight: 700 }}>⚠️ Negative Balance: Top-up required</span>
                    ) : (
                      "Total Credited − WhatsApp Usage"
                    )}
                  </div>
                </div>
                <div
                  style={{
                    width: "46px",
                    height: "46px",
                    borderRadius: "14px",
                    background: metrics.remainingBalance < 0 ? "rgba(255,77,79,0.2)" : `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldenrod})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i
                    className="fas fa-coins"
                    style={{
                      color: metrics.remainingBalance < 0 ? "#ff6b6b" : COLORS.darkGreen,
                      fontSize: "20px",
                    }}
                  ></i>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* CONTROLS (SEARCH & TYPE FILTER TABS) */}
        <div
          className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 p-3"
          style={{
            background: COLORS.darkGreen,
            borderRadius: "16px",
            border: `1px solid ${COLORS.calPolyGreen}`,
          }}
        >
          {/* Tabs */}
          <div className="d-flex align-items-center gap-1" style={{ background: COLORS.bgDark, padding: "4px", borderRadius: "10px", border: `1px solid ${COLORS.calPolyGreen}` }}>
            {["ALL", "CREDIT", "DEBIT"].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setFilterType(tab);
                  setCurrentPage(1);
                }}
                className="btn btn-sm"
                style={{
                  border: "none",
                  padding: "6px 16px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: 700,
                  transition: "0.2s ease",
                  background: filterType === tab ? `linear-gradient(135deg, ${COLORS.gold}, ${COLORS.goldenrod})` : "transparent",
                  color: filterType === tab ? COLORS.darkGreen : COLORS.vanilla, marginTop:0
                }}
              >
                {tab === "ALL" ? "All Statements" : tab === "CREDIT" ? `Recharges (${metrics.totalCreditsCount})` : "WhatsApp Debits"}
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div style={{ position: "relative", minWidth: "280px" }}>
            <i
              className="fas fa-search"
              style={{
                position: "absolute",
                left: "12px",
                top: "50%",
                transform: "translateY(-50%)",
                color: TEXT_SECONDARY,
                fontSize: "12px",
              }}
            />
            <input
              type="text"
              placeholder="Search date, reference, or note..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: "100%",
                padding: "8px 12px 8px 34px",
                borderRadius: "10px",
                border: `1px solid ${COLORS.calPolyGreen}`,
                background: COLORS.bgDark,
                color: COLORS.vanilla,
                fontSize: "12px",
                outline: "none",
              }}
            />
          </div>
        </div>

        {/* TRANSACTIONS TABLE WITH SR NO */}
        <Card
          style={{
            borderRadius: "16px",
            border: `1px solid ${COLORS.calPolyGreen}`,
            background: COLORS.darkGreen,
            overflow: "hidden",
            boxShadow: "0 6px 18px rgba(0,0,0,0.3)",
          }}
        >
          {loading ? (
            <div className="py-5 text-center">
              <i className="fas fa-spinner fa-spin fa-2x mb-2" style={{ color: COLORS.gold }}></i>
              <p style={{ color: TEXT_SECONDARY, fontSize: "13px" }}>Loading unified statement feed...</p>
            </div>
          ) : currentRows.length === 0 ? (
            <div className="py-5 text-center">
              <i className="fas fa-receipt fa-2x mb-3" style={{ color: COLORS.calPolyGreen }}></i>
              <h6 style={{ color: COLORS.vanilla, fontWeight: 700 }}>No Records Found</h6>
              <p style={{ color: TEXT_SECONDARY, fontSize: "12px", margin: 0 }}>
                {searchTerm || filterType !== "ALL"
                  ? "No statements match your filter criteria."
                  : "All top-up deposits and daily WhatsApp message expenses will appear here."}
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle mb-0" style={{ color: COLORS.vanilla, fontSize: "13px" }}>
                <thead>
                  <tr
                    style={{
                      background: `linear-gradient(135deg, ${COLORS.bgDark} 0%, ${COLORS.darkGreen} 100%)`,
                      borderBottom: `1px solid ${COLORS.calPolyGreen}`,
                      color: TEXT_SECONDARY,
                      fontSize: "11px",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    <th style={{ padding: "14px 16px", width: "70px" }}>Sr. No</th>
                    <th style={{ padding: "14px 16px" }}>Date & Time</th>
                    <th style={{ padding: "14px 16px" }}>Type</th>
                    <th style={{ padding: "14px 16px" }}>Description</th>
                    <th style={{ padding: "14px 16px" }}>Payment Ref</th>
                    <th style={{ padding: "14px 16px" }}>Status</th>
                    <th style={{ padding: "14px 16px" }} className="text-end">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentRows.map((tx, idx) => {
                    const isCredit = tx.type === "CREDIT";
                    const serialNumber = (currentPage - 1) * rowsPerPage + idx + 1;

                    return (
                      <tr
                        key={tx.id || idx}
                        style={{
                          borderBottom: `1px solid rgba(21, 83, 50, 0.4)`,
                          background: idx % 2 === 0 ? "transparent" : "rgba(3, 28, 23, 0.35)",
                          transition: "background 0.2s ease",
                        }}
                      >
                        {/* SR NO */}
                        <td style={{ padding: "14px 16px", fontWeight: 700, color: COLORS.gold }}>
                          #{serialNumber}
                        </td>

                        {/* DATE & TIME */}
                        <td style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                          <span style={{ fontWeight: 600 }}>{tx.formattedDate}</span>
                        </td>

                        {/* TYPE */}
                        <td style={{ padding: "14px 16px" }}>
                          <span
                            style={{
                              padding: "4px 10px",
                              borderRadius: "20px",
                              fontSize: "10px",
                              fontWeight: 800,
                              textTransform: "uppercase",
                              background: isCredit ? "rgba(21, 83, 50, 0.6)" : "rgba(209, 165, 5, 0.2)",
                              color: isCredit ? COLORS.gold : COLORS.goldenrod,
                              border: `1px solid ${isCredit ? COLORS.calPolyGreen : COLORS.goldenrod}`,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <i className={`fas ${isCredit ? "fa-arrow-down" : "fa-arrow-up"}`}></i>
                            {isCredit ? "Credit" : "Debit"}
                          </span>
                        </td>

                        {/* DESCRIPTION */}
                        <td style={{ padding: "14px 16px" }}>
                          <div style={{ fontWeight: 600, color: COLORS.vanilla }}>
                            {tx.description}
                          </div>
                        </td>

                        {/* PAYMENT REF */}
                        <td style={{ padding: "14px 16px" }}>
                          <div
                            onClick={() => handleCopyId(tx.paymentId)}
                            title="Click to copy Reference"
                            style={{
                              fontFamily: "monospace",
                              fontSize: "11px",
                              color: COLORS.vanilla,
                              cursor: "pointer",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                              background: COLORS.bgDark,
                              padding: "4px 8px",
                              borderRadius: "6px",
                              border: `1px solid ${COLORS.calPolyGreen}`,
                            }}
                          >
                            <span>{tx.paymentId}</span>
                            <i className="far fa-copy text-muted" style={{ fontSize: "10px" }} />
                          </div>
                        </td>

                        {/* STATUS */}
                        <td style={{ padding: "14px 16px" }}>
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: 700,
                              color: tx.status === "Failed" ? "#ff6b6b" : COLORS.gold,
                            }}
                          >
                            ● {tx.status}
                          </span>
                        </td>

                        {/* AMOUNT */}
                        <td style={{ padding: "14px 16px" }} className="text-end">
                          <span
                            style={{
                              fontWeight: 800,
                              fontSize: "15px",
                              color: isCredit ? COLORS.gold : COLORS.goldenrod,
                            }}
                          >
                            {isCredit ? "+" : "-"}₹{Number(tx.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* PAGINATION CONTROLS */}
          {totalPages > 1 && (
            <div
              className="d-flex justify-content-between align-items-center p-3 flex-wrap gap-2"
              style={{
                borderTop: `1px solid ${COLORS.calPolyGreen}`,
                background: COLORS.bgDark,
              }}
            >
              <span style={{ color: TEXT_SECONDARY, fontSize: "12px" }}>
                Showing page {currentPage} of {totalPages} ({filteredRecords.length} records)
              </span>

              <div className="d-flex gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="btn btn-sm"
                  style={{
                    background: COLORS.darkGreen,
                    border: `1px solid ${COLORS.calPolyGreen}`,
                    color: COLORS.vanilla,
                    fontSize: "11px",
                    borderRadius: "6px",
                    padding: "6px 14px",
                  }}
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="btn btn-sm"
                  style={{
                    background: COLORS.darkGreen,
                    border: `1px solid ${COLORS.calPolyGreen}`,
                    color: COLORS.vanilla,
                    fontSize: "11px",
                    borderRadius: "6px",
                    padding: "6px 14px",
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default WalletHistory;